// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
import { AUCTION_LOT_INFO_READ, LOT_EDIT_MODAL_READ } from './lots';
import type { AdminVerbSpec, TenantSource } from './define-admin-route';

// BL-1832 proof set: the seven `settings/<area>` routes share ONE handler
// factory (`create-area-route-handlers.ts`), so they are classified together —
// a factory cannot serve a gated route and an ungated one without keeping two
// auth paths alive, which ADR 044 — Must Consolidate bans. Named once here so
// the thirteen verbs below cannot drift apart.
// The grants map onto the exact permissions the factory checked by hand
// (`auctions.view` / `auctions.manage`), so no caller gains or loses access.
const AUCTION_SETTINGS_READ = {
  grant: 'auction.read',
  tenant: { from: 'record', table: 'auction', param: 'id' },
} as const satisfies Pick<AdminVerbSpec, 'grant' | 'tenant'>;
const AUCTION_SETTINGS_WRITE = {
  grant: 'auction.update',
  tenant: { from: 'record', table: 'auction', param: 'id' },
} as const satisfies Pick<AdminVerbSpec, 'grant' | 'tenant'>;
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { AuctionFilterOptionsDto } from '../../schemas/responses/auction-filter-options';
import type { AuctionListCounts, AuctionListPage } from '../../schemas/responses/auction-list';
import type { LotDisposeDto } from '../../schemas/responses/lot-dispose';
import type { LotReserveDto, ReserveLiftAudienceDto } from '../../schemas/responses/lot-reserve';
import type { TimedClerkBidDto } from '../../schemas/responses/timed-clerk-bid';
import type { AuctionBidsFeedDto } from '../../schemas/responses/auction-bids-feed';
import type { AuctionSettingsSummaryDto } from '../../schemas/responses/auction-settings-summary';
import type { AuctionSettingsReadinessDto } from '../../schemas/responses/auction-settings-readiness';
import type {
  AuctionBasicsSettingsDto,
  AuctionBiddingSettingsDto,
  AuctionContactSettingsDto,
  AuctionTermsSettingsDto,
  AuctionMediaSettingsDto,
  AuctionPricingSettingsDto,
  AuctionRegistrationSettingsDto,
} from '../../schemas/responses/auction-settings';
import type { AuctionTransitionDto } from '../../schemas/responses/auction-transition';
import type { AuctionWatchersDto } from '../../schemas/responses/auction-watchers';
import type { AuctionReschedulePreviewDto } from '../../schemas/responses/auction-reschedule-preview';
import type { NotificationTemplatePreviewDto } from '../../schemas/responses/notification-template-preview';
import type {
    AuctionDateChangeNotificationPreviewDto,
    AuctionDateChangeNotificationSendDto,
    AuctionDateChangeNotificationDismissDto,
} from '../../schemas/responses/auction-date-change-notification';
import type {
  AuctionBroadcastAudienceDto,
  AuctionBroadcastListDto,
  AuctionBroadcastSendDto,
} from '../../schemas/responses/auction-broadcast';
import type { AuctionBidIncrementsDto } from '../../schemas/responses/auction-bid-increments';
import type { AuctionBillingProgressDto, AuctionBillingRetryDto } from '../../schemas/responses/auction-billing';
import type { AuctionSummaryDto, AuctionSummaryRangeDto } from '../../schemas/responses/auction-summary';
import type { AuctionChromeDto } from '../../schemas/responses/auction-chrome';
import type { RegistrationTermsAcceptancesPayload, TermsAcceptanceProofUrlDto } from '../../schemas/responses/terms-acceptance';
import type {
    AuctionTermsAttachmentDto,
    TermsChangeImpactDto,
} from '../../schemas/responses/auction-terms';
import type { Auction, AuctionDetail } from '../../schemas/responses/auction';
import type { AuctionSellerRosterResult } from '../../schemas/responses/auction-seller-roster';
import type { AuctionScopedBuyersPremiumWriteResult } from '../../schemas/responses/buyers-premium-write';
import type {
    AuctionSellerCommissionReadResult,
    AuctionSellerCommissionWriteResult,
} from '../../schemas/responses/commission-write';
import type { AuctionRingsListDto } from '../../schemas/responses/auction-ring-lots';
import type { RingRosterConfigDto } from '../../schemas/responses/auction-ring-roster';
import type { LotOrderPageDto, LotOrderLocateDto } from '../../schemas/responses/lot-order';
import type { LotReorderResultDto, LotReorderTargetDto } from '../../schemas/responses/lot-reorder';
import type {
  ChangeCloseTimeDto,
  ChangeCloseTimeSeedDto,
} from '../../schemas/responses/change-close-time';
import type { ReopenLotDto } from '../../schemas/responses/reopen-lot';
import type { ReverseSaleContextDto } from '../../schemas/responses/reverse-sale-context';
import type { ReverseSaleDto } from '../../schemas/responses/reverse-sale';
import type { SellLotDto } from '../../schemas/responses/sell-lot';
import type {
  ClerkCatalogDto,
  ClerkLotCatalogDetailsDto,
} from '../../schemas/responses/clerk-catalog';
import type { ClerkRailRosterDto } from '../../schemas/responses/clerk-rail-roster';
import type { ClerkBidderSearchDto } from '../../schemas/responses/clerk-bidder-search';
import type { ClerkPendingRegistrationsPageDto } from '../../schemas/responses/clerk-pending-registrations';
import type { AdminAddBuyerResultDto } from '../../schemas/responses/bid-registration';
import type { AuctionBidderDetailDto, AuctionBiddersReadPayload } from '../../schemas/responses/auction-bidder';
import type { AuctionTransitionCheckResponse } from '../../schemas/responses/auction-transition-check';
import type { AuctionTransitionPreviewPayload } from '../../schemas/responses/auction-transition-preview';
import type { BidChangePreviewDto } from '../../schemas/responses/bid-change-preview';
import type {
  BidEditDto,
  BidWithdrawDto,
  MaxBidCancelDto,
  MaxBidCorrectionDto,
} from '../../schemas/responses/bid-corrections';

/**
 * A route nested under `/api/auctions/:id/lots/:lotId` (BL-1833 PR 3b). The
 * LOT is the gated record — it carries its own tenant — and `within` makes a
 * lot addressed under any other auction (or a home lot) the gate's 404, so no
 * handler re-checks the pair by hand.
 */
/** The auction named by `:id` (BL-1833 PR 3c). */
const AUCTION = { from: 'record', table: 'auction', param: 'id' } as const satisfies TenantSource;

/** Ring configuration: the clerk's sequencing grant or the auction editor's (CLERKING_MANAGE | AUCTIONS_MANAGE). */
const RING_WRITE = { anyOf: ['lot.sequence', 'auction.update'] } as const satisfies AdminVerbSpec['grant'];

const LOT_IN_AUCTION = {
  from: 'record',
  table: 'lot',
  param: 'lotId',
  within: { column: 'auctionId', param: 'id' },
} as const satisfies TenantSource;

export const auctions = {
  // BL-1286: read-only auction lifecycle eligibility. Answers "what can I do to
  // these auctions right now, and why not?" from ONE source — the pure
  // `canApplyAuctionTransition` guard — so the BL-1292 status control and the
  // server that performs a transition can never drift. Bulk-shaped by
  // construction (ADR 033 Decision 10: single = bulk of one).
  transition_check: defineAdminRoute({
      path: '/api/auctions/transition-check',
      params: {},
      // `scoped`: the ids may span the caller's tenants and include archived
      // auctions (a restore row); the service filters to the gate's set.
      methods: { POST: { grant: 'auction.read', tenant: { from: 'scoped' }, response: responseType<AuctionTransitionCheckResponse>() } },
    }),
  // BL-1295: the EFFECTS axis alongside `transition_check`'s eligibility axis.
  // Answers "what will this transition actually do to this selection?" from the
  // same `TransitionDef` the apply path runs, so the confirmation modal cannot
  // promise something different from what executes (ADR 033 Decision 7).
  transition_preview: defineAdminRoute({
      path: '/api/auctions/transition-preview',
      params: {},
      methods: { POST: { grant: 'auction.read', tenant: { from: 'scoped' }, response: responseType<AuctionTransitionPreviewPayload>() } },
    }),
  // BL-2769: the Status pill's per-status counts, re-read on every filter change (the inventory
  // counts route's twin). Same read set as the list it counts.
  counts: defineAdminRoute({
      path: '/api/auctions/counts',
      params: {},
      methods: { GET: { grant: LOT_EDIT_MODAL_READ, tenant: { from: 'input', field: 'tenantId', in: 'query' }, response: responseType<AuctionListCounts>() } },
    }),
  // BL-2901 (PRJ-108 SUMM-3) — the Auction summary over every auction closed in a period (by close
  // date on the house's calendar). The sibling of `id.summary`: a range has no record, so the
  // tenant comes from the query. The handler also requires settlement.read (T1).
  summary: defineAdminRoute({
      path: '/api/auctions/summary',
      params: {},
      methods: {
        GET: {
          grant: 'invoice.read',
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<AuctionSummaryRangeDto>(),
          query: { tenantId: '', close: '', close_range: '' },
        },
      },
    }),
  filter_options: defineAdminRoute({
      path: '/api/auctions/filter-options',
      params: {},
      // BL-2358: the Auctions list page's Sales rep options. The page opens to an
      // auctions viewer or an inventory viewer (a Cataloger), so its read does too.
      methods: { GET: { grant: { anyOf: ['auction.read', 'inventory.read'] }, tenant: { from: 'input', field: 'tenantId', in: 'query' }, response: responseType<AuctionFilterOptionsDto>() } },
    }),
  id: {
    notification_templates: {
      index: defineAdminRoute({
          path: '/api/auctions/:id/notification-templates',
          params: { id: '' },
          // GET was membership-only; the Notifications tab is an Admin page.
          // The template-belongs-to-auction check stays in the service.
          methods: {
            GET: { grant: 'auction.read', tenant: AUCTION },
            POST: { grant: 'auction.update', tenant: AUCTION },
          },
        }),
      template: defineAdminRoute({
          path: '/api/auctions/:id/notification-templates/:templateId',
          params: { id: '', templateId: '' },
          methods: {
            PATCH: { grant: 'auction.update', tenant: AUCTION },
            DELETE: { grant: 'auction.update', tenant: AUCTION },
          },
        }),
    },
    buyers_premium: defineAdminRoute({
        path: '/api/auctions/:id/buyers-premium',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-984): the stored auction rule (or
        // `null` once the override is removed) plus the uninvoiced-sold-lot
        // warning count.
        methods: {
          PUT: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionScopedBuyersPremiumWriteResult>() },
        },
      }),
    bidders: {
      index: defineAdminRoute({
          path: '/api/auctions/:id/bidders',
          params: { id: '' },
          // BL-1833 AC 10–12. BL-1314 moved approve/reject onto
          // `auctions.approve_bidders` before the gate existed; these are that
          // decision, now enforced by declaration rather than by hand.
          methods: {
            // The roster — buyer names and contact details. Was tenant-only:
            // any member, a Cataloger included, could read it through the API.
            GET: {
              grant: 'auction.read',
              tenant: { from: 'record', table: 'auction', param: 'id' },
              // BL-2022 (ADR 027 — Response Contract): the roster-or-page union.
              response: responseType<AuctionBiddersReadPayload>(),
            },
            PATCH: {
              grant: 'auction.approve_bidders',
              tenant: { from: 'record', table: 'auction', param: 'id' },
            },
          },
        }),
    },
    bids: {
      feed: defineAdminRoute({
          path: '/api/auctions/:id/bids/feed',
          params: { id: '' },
          // No screen calls it (BL-1294 retired its hook); the QA agent grades
          // bids through it, and no other read returns an auction's bid ledger.
          // BL-2010: bound to its DTO (ADR 027 — Response Contract).
          methods: { GET: { grant: 'lot.read', tenant: AUCTION, response: responseType<AuctionBidsFeedDto>() } },
        }),
    },
    // BL-1575 (ADR 057 — Operator Sends): the pending date-change announcement.
    // GET feeds the confirmation modal; POST is the operator-authorised send.
    // The two share ONE audience query, so the counts shown and the recipients
    // notified cannot disagree.
    // BL-1572: the read-only disclosure behind the "Reschedule every lot?"
    // dialog. Moving an auction's first lot close re-times every unclosed lot,
    // and the recompute runs as an in-transaction finalizer — so there is no
    // "after" to read once the write lands. This answers it beforehand, off the
    // same computation the save runs.
    reschedule_preview: defineAdminRoute({
        path: '/api/auctions/:id/reschedule-preview',
        params: { id: '' },
        methods: {
          POST: { grant: 'auction.read', tenant: AUCTION, response: responseType<AuctionReschedulePreviewDto>() },
        },
      }),
    // BL-1833 — the auction-scope notification preview: the Notifications tab's
    // draft render (sample values) and the date-change modal's send gate
    // (`values: 'live'`, ADR 057 — Operator Sends). Split off the shared
    // `notification_templates.preview` so the auction can be the gated record.
    notification_preview: defineAdminRoute({
        path: '/api/auctions/:id/notification-preview',
        params: { id: '' },
        methods: {
          POST: { grant: 'auction.update', tenant: AUCTION, response: responseType<NotificationTemplatePreviewDto>() },
        },
      }),
    date_change_notification: defineAdminRoute({
        path: '/api/auctions/:id/date-change-notification',
        params: { id: '' },
        methods: {
          // auctions.manage on both, as before: GET is the send's own preview.
          GET: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionDateChangeNotificationPreviewDto>() },
          POST: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionDateChangeNotificationSendDto>() },
          // BL-2110: dismiss the pending notice without a send — the same grant
          // as the send it stands in for (ADR 057 — Operator Sends § 1b).
          DELETE: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionDateChangeNotificationDismissDto>() },
        },
      }),
    // BL-1488 (PRJ-59 3/7): the clerk broadcast. GET is the clerk's log; POST
    // is the send — ONE transaction writing the audited parent, every
    // recipient's in-app row + unread counts, and the email/SMS outbox rows.
    broadcasts: defineAdminRoute({
        path: '/api/auctions/:id/broadcasts',
        params: { id: '' },
        methods: {
          // The clerk mapping: clerking.view has no grant, so reads take lot.read
          // (the same Admin-only audience); the send is the clerk's lot.hammer.
          GET: { grant: 'lot.read', tenant: AUCTION, response: responseType<AuctionBroadcastListDto>() },
          POST: { grant: 'lot.hammer', tenant: AUCTION, response: responseType<AuctionBroadcastSendDto>() },
        },
      }),
    // BL-2229 (ADR 057 — Operator Sends § 2): who a broadcast sent now would be
    // addressed to, counted by the send's own audience call. The send's grant,
    // not the log's: the count serves whoever can press Send, and an audience
    // size is privileged house data.
    broadcast_audience: defineAdminRoute({
        path: '/api/auctions/:id/broadcasts/audience',
        params: { id: '' },
        methods: {
          GET: { grant: 'lot.hammer', tenant: AUCTION, response: responseType<AuctionBroadcastAudienceDto>() },
        },
      }),
    // BL-1669 (ADR 029): the Settings index's ONE read — the eight area
    // summaries composed server-side plus the current phase for the Status
    // card. The index reads nothing else.
    settings_summary: defineAdminRoute({
        path: '/api/auctions/:id/settings-summary',
        params: { id: '' },
        methods: { GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<AuctionSettingsSummaryDto>() } },
      }),
    // BL-1670 — the Settings index's ONE write: tick / untick an area's
    // "Ready?" box. Echoes the auction's full readiness list so the client
    // adopts the server's truth (no optimistic flip). The read rides
    // `settings_summary` above — there is no GET here.
    settings_readiness: defineAdminRoute({
        path: '/api/auctions/:id/settings-readiness',
        params: { id: '' },
        methods: { PUT: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionSettingsReadinessDto>() } },
      }),
    // BL-1689 (PRJ-69): the six STAGED Settings areas — one GET + one PUT each,
    // scoped to that page's fields (ADR 026: one logical save = one request).
    // The PUT body is the area's Zod schema in
    // `@auctogon/core-admin/schemas/auction-settings`, the same object the
    // page's zodResolver runs (ADR 016). Sellers is action-shaped (BL-1690)
    // and keeps its own routes; Terms became staged in BL-1838 for the invoice
    // fine print pair, while its ATTACHMENT still writes through
    // `terms_version` below.
    settings: {
      basics: defineAdminRoute({
        path: '/api/auctions/:id/settings/basics',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionBasicsSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionBasicsSettingsDto>() },
        },
      }),
      bidding: defineAdminRoute({
        path: '/api/auctions/:id/settings/bidding',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionBiddingSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionBiddingSettingsDto>() },
        },
      }),
      // The buyer's premium writes through `auctions.id.buyers_premium`; the
      // PUT saves the area's one staged field, the minimum commission per
      // auction (BL-2315).
      pricing: defineAdminRoute({
        path: '/api/auctions/:id/settings/pricing',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionPricingSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionPricingSettingsDto>() },
        },
      }),
      registration: defineAdminRoute({
        path: '/api/auctions/:id/settings/registration',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionRegistrationSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionRegistrationSettingsDto>() },
        },
      }),
      media: defineAdminRoute({
        path: '/api/auctions/:id/settings/media',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionMediaSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionMediaSettingsDto>() },
        },
      }),
      contact: defineAdminRoute({
        path: '/api/auctions/:id/settings/contact',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionContactSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionContactSettingsDto>() },
        },
      }),
      terms: defineAdminRoute({
        path: '/api/auctions/:id/settings/terms',
        params: { id: '' },
        methods: {
          GET: { ...AUCTION_SETTINGS_READ, response: responseType<AuctionTermsSettingsDto>() },
          PUT: { ...AUCTION_SETTINGS_WRITE, response: responseType<AuctionTermsSettingsDto>() },
        },
      }),
    },
    // BL-1292: the ONE endpoint for every auction lifecycle transition — the
    // write half of `transition_check` above, driving the same guard through
    // the ADR-033 effect engine.
    //
    // BL-1294 discharged half of BL-1292's declared residue: `timed-controls/
    // pause|resume` are DELETED, and their `auditVerbOverride: 'paused'|'resumed'`
    // moved onto this route (they were the repo's only emitters of those two
    // verbs). BL-1833 PR 3c-1 deleted `status-with-cascade` and `restore`, the
    // last duplicate doors: archive and restore are this route's `soft-delete`
    // and `restore`.
    transition: defineAdminRoute({
        path: '/api/auctions/:id/transition',
        params: { id: '' },
        // The floor every action shares (auctions.manage | clerking.manage |
        // auctions.close_stage, BL-2283); the per-action narrowing
        // (`permissionFor` in `isomorphic/auth/auction-transition-permissions`:
        // pause/resume need clerking.manage, open either, set-close-stage
        // auctions.close_stage, the rest auctions.manage) stays in the handler —
        // declared residue, since the action is in the body.
        // `withDeleted`: restore acts on an archived auction.
        methods: {
          POST: {
            grant: { anyOf: ['auction.update', 'lot.hammer', 'auction.close_stage'] },
            tenant: { from: 'record', table: 'auction', param: 'id', withDeleted: true },
            response: responseType<AuctionTransitionDto>(),
          },
        },
      }),
    // BL-1644: the INPUTS to increment resolution for one auction, plus the
    // named lot's override. Separate from `detail` because the lot editor needs
    // only the ladder and `detail` is a heavy full-auction read (sellers, terms
    // version, lot counts, commission disclosure) it would otherwise drag in on
    // every open. Resolution itself stays client-side via the Money ladder's incrementAt
    // (ADR 010) — the price being asked about changes per keystroke, the ladder
    // does not.
    // BL-1688 (PRJ-69): the auction-detail LAYOUT's read — heading, derived
    // phase, format, date line, location label, pending badge count, liveSafe.
    // Separate from `detail` (the heavy full-auction read the segments use) so
    // the header never carries a second copy of a value a sub-page edits.
    chrome: defineAdminRoute({
        path: '/api/auctions/:id/chrome',
        params: { id: '' },
        methods: {
          // `withDeleted`: the archived auction view loads its header too.
          // BL-2349: + inventory.read — a Cataloger's Lot Info tab renders under
          // this layout read (no bidder contact data: the pending count is a number).
          GET: { grant: AUCTION_LOT_INFO_READ, tenant: { from: 'record', table: 'auction', param: 'id', withDeleted: true }, response: responseType<AuctionChromeDto>() },
        },
      }),
    // BL-2955 — a short-lived link to open one of the auction's documents (302 to
    // the signed link, so no response DTO). AUCTION_LOT_INFO_READ (Michael Lawrie,
    // 2026-11-03): the lot reads that also see an auction's header. Today the only caller is
    // Auction Settings; the lot drawer lists lot-scope documents only.
    document_download: defineAdminRoute({
      path: '/api/auctions/:id/documents/:documentId/download',
      params: { id: '', documentId: '' },
      methods: {
        GET: { grant: AUCTION_LOT_INFO_READ, tenant: { from: 'record', table: 'auction', param: 'id' } },
      },
    }),
    // BL-2034 — the auction's lot-billing progress and failed lots (the header's
    // "Billing lots · 640 of 2,000" line and "N lots failed to bill" notice).
    // Invoice data, so invoice.read; another house's auction is a 404.
    billing: defineAdminRoute({
        path: '/api/auctions/:id/billing',
        params: { id: '' },
        methods: {
          GET: { grant: 'invoice.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<AuctionBillingProgressDto>() },
        },
      }),
    // BL-2896 (PRJ-108 SUMM-1) — the auction's Accounting › Auction summary. It rolls up
    // both invoices and settlements, so the handler also requires settlement.read (T1);
    // another house's auction is a 404. BL-2900: `since` (an ISO instant) picks Changes since's moment.
    summary: defineAdminRoute({
        path: '/api/auctions/:id/summary',
        params: { id: '' },
        methods: {
          GET: { grant: 'invoice.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<AuctionSummaryDto>(), query: { since: '' } },
        },
      }),
    // BL-2399 — Retry all for one reason: bills that group's failed lots now,
    // buyer by buyer, through Add to invoice's apply — so Add to invoice's grant.
    billing_retry: defineAdminRoute({
        path: '/api/auctions/:id/billing/retry',
        params: { id: '' },
        methods: {
          POST: { grant: 'invoice.create', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<AuctionBillingRetryDto>() },
        },
      }),
    bid_increments: defineAdminRoute({
        path: '/api/auctions/:id/bid-increments',
        params: { id: '' },
        methods: {
          // The lot and item forms read it (the opening-bid notice), which
          // Catalogers and Accounting reach from /inventory and invoices.
          GET: {
            grant: LOT_EDIT_MODAL_READ,
            tenant: AUCTION,
            response: responseType<AuctionBidIncrementsDto>(),
            // BL-2253: no `lotId` — the lot's override rides the admin
            // lot-state stream the editor subscribes to (rule #32). BL-2261:
            // no ladder — it rides the auction snapshot; only the currency stays.
          },
        },
      }),
    detail: defineAdminRoute({
        path: '/api/auctions/:id',
        params: { id: '' },
        // ADR023 (BL-701): DELETE (auction soft-delete) removed — broken feature
        // reworked under ADR018 (BL-724).
        // BL-1691: PATCH removed — every auction write has a per-area endpoint
        // (`settings.<area>`, `sellers`, `terms_version`, `buyers_premium`,
        // `transition`). With no method here the typed client cannot
        // express the call (ADR 044 — Must Consolidate).
        // ADR 027 / BL-901: GET returns the single-source `Auction` DTO.
        methods: {
          // BL-1833 AC 14. Two roles read this with no permission in common:
          // Admin's auction pages and clerk console (auctions.view), and
          // Accounting's invoice + settlement modals (buyer's-premium terms,
          // location) — so any one of the three. `withDeleted`: the detail page
          // loads an archived auction read-only to offer Restore (BL-724).
          GET: {
            // BL-2349 (option B1, Michael Lawrie 2026-10-08): + inventory.read —
            // the Lot Info tab's segment reads this, so a Cataloger does too.
            // Reverses BL-1833 AC 14's Cataloger refusal; seller commission stays
            // redacted for them (`applyCommissionDisclosure`).
            grant: { anyOf: ['auction.read', 'invoice.read', 'settlement.read', 'inventory.read'] },
            tenant: { from: 'record', table: 'auction', param: 'id', withDeleted: true },
            // BL-1918: the detail read also carries the auction's currency.
            response: responseType<AuctionDetail>(),
          },
        },
      }),
    // BL-1254 — the clerk console's items catalog: static lot identity only.
    // The live fields (close time, accepted bid, bid count, sort order, decision
    // stamps) reach the console over the admin lot-state realtime stream and are
    // deliberately absent from this payload (rule #32).
    lots: {
      // BL-1833 PR 3b-1: `index` (GET /api/auctions/:id/lots) deleted — its last
      // client wrapper had no importer. BL-1338 and BL-1534 had already retired
      // its POST and PUT (ADR 044 — Must Consolidate).
      // BL-1180: the reorder-intent endpoint — a paged Lot-Order client sends a
      // single move ({movingLotIds, toPosition}) resolved against the true
      // sequence, instead of the full-ordering `lots` PUT (which a paged client
      // can't build). Ack-only on success; the mutated rows come back for a
      // no-refetch patch.
      reorder: defineAdminRoute({
          path: '/api/auctions/:id/lots/reorder',
          params: { id: '' },
          // clerking.manage OR auctions.manage, as the handler checked by hand.
          methods: {
            PUT: {
              grant: { anyOf: ['lot.sequence', 'lot.update'] },
              tenant: { from: 'record', table: 'auction', param: 'id' },
              response: responseType<LotReorderResultDto>(),
            },
          },
        }),
      // BUG-1974: before a live sale's typed plain lot number is held at the
      // interception, the client asks whether anyone but the movers holds that
      // number — a free one is a placement (only the movers change), a taken one
      // a renumber. A read, but POST so the mover list rides the reorder body's
      // own schema. Gated exactly like the move it precedes.
      reorder_target: defineAdminRoute({
          path: '/api/auctions/:id/lots/reorder/target',
          params: { id: '' },
          methods: {
            POST: {
              grant: { anyOf: ['lot.sequence', 'lot.update'] },
              tenant: { from: 'record', table: 'auction', param: 'id' },
              response: responseType<LotReorderTargetDto>(),
            },
          },
        }),
      lotId: {
        // BL-1546 (AC 16): `bidders` retired — the Bidders panel is gone; the
        // bid record reads contact from `GET /api/lots/:id/bidder-identity`.
        /**
         * BL-1256 — the clerk catalog's expand-in-place detail.
         *
         * Separate from the general-purpose single-lot read on purpose: that
         * one returns `reserve` / `hammerPrice` / `status`, all of which are on
         * the admin lot-state projection. The clerk console subscribes to that
         * channel, so this endpoint carries the STATIC cataloged fields only
         * (rule #32). See `ClerkLotCatalogDetailsDto`.
         */
        catalog_details: defineAdminRoute({
            path: '/api/auctions/:id/lots/:lotId/catalog-details',
            params: { id: '', lotId: '' },
            // clerking.view has no grant; `lot.read` is auctions.view — the
            // same holder (Admin) today (BL-1833).
            methods: {
              GET: { grant: 'lot.read', tenant: LOT_IN_AUCTION, response: responseType<ClerkLotCatalogDetailsDto>() },
            },
          }),
        bids: {
          bidId: {
            edit: defineAdminRoute({
                path: '/api/auctions/:id/lots/:lotId/bids/:bidId/edit',
                params: { id: '', lotId: '', bidId: '' },
                // clerking.manage, as the handler checked by hand. The bid-in-lot
                // check stays in BidEditService (the bid is not the gated record).
                // BL-2125 (ADR 027 — Response Contract): the correction outcome.
                methods: { POST: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<BidEditDto>() } },
              }),
            withdraw: defineAdminRoute({
                path: '/api/auctions/:id/lots/:lotId/bids/:bidId/withdraw',
                params: { id: '', lotId: '', bidId: '' },
                // BL-2125: `newPrice` as MoneyWire.
                methods: { POST: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<BidWithdrawDto>() } },
              }),
            /**
             * BL-1258 AC 7 — the server-derived consequence of a pending edit
             * or withdrawal. `amount` present = edit preview; absent = withdrawal.
             */
            change_preview: defineAdminRoute({
                path: '/api/auctions/:id/lots/:lotId/bids/:bidId/change-preview',
                params: { id: '', lotId: '', bidId: '' },
                // clerking.manage: the preview discloses the consequence of a
                // correction only the clerk can make.
                methods: {
                  GET: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<BidChangePreviewDto>() },
                },
              }),
          },
        },
        // BL-2115 (ADR 027 — Response Contract): the bid / maximum / pre-bid
        // answer is `TimedClerkBidDto` under `data`, every amount MoneyWire; the
        // floor-carrying 400 is `TimedClerkBidRejectedBody`.
        timed_clerk_bid: defineAdminRoute({
            path: '/api/auctions/:id/lots/:lotId/timed-clerk-bid',
            params: { id: '', lotId: '' },
            methods: { POST: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<TimedClerkBidDto>() } },
          }),
        dispose: defineAdminRoute({
            path: '/api/auctions/:id/lots/:lotId/dispose',
            params: { id: '', lotId: '' },
            // BL-1809 (ADR 027 — Response Contract): the write acknowledgement.
            // clerking.manage, as before. A `sold` disposition also syncs the
            // invoice and settlement lines; only Admin holds this grant today.
            methods: { POST: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<LotDisposeDto>() } },
          }),
        change_close_time: defineAdminRoute({
            path: '/api/auctions/:id/lots/:lotId/change-close-time',
            params: { id: '', lotId: '' },
            methods: {
              // BL-1299 AC 11: the modal's one-shot form seed, paired with the
              // write below so one route owns both halves of the facet.
              GET: { grant: 'lot.reschedule', tenant: LOT_IN_AUCTION, response: responseType<ChangeCloseTimeSeedDto>() },
              POST: { grant: 'lot.reschedule', tenant: LOT_IN_AUCTION, response: responseType<ChangeCloseTimeDto>() },
            },
          }),
        // BL-1833 PR 3b-1: `index` (GET + PUT /api/auctions/:id/lots/:lotId)
        // deleted. The PUT was a second lot-edit door beside PATCH
        // /api/lots/:id; its one caller, the clerk increment ladder, now saves
        // through that PATCH (ADR 044 — Must Consolidate). BL-1431 had already
        // moved its DELETE to POST /api/lots/batch.
        /**
         * The lot's max-bid surface. BL-1340 retired the GET (the append-only
         * history list): its only caller was orphaned when BL-1294 deleted
         * Clerking (Classic). BUG-1732 deleted the POST: it keyed its bidder
         * gate on the STAFF session's id, so it refused every request with
         * "You must be registered and approved to bid". A clerk places a max
         * through `timed_clerk_bid` with `kind: 'max'`. Only the corrections
         * remain (ADR 044 — Must Consolidate).
         */
        max_bids: {
          /**
           * BUG-1373 — the two max-bid CORRECTIONS. They used to live at `/api/pre-bids/:id`
           * under a noun that stopped being true when the gate widened from
           * pre-bidding to any lot taking bids; that route is deleted, with no
           * compat alias (ADR 044).
           */
          maxBidId: defineAdminRoute({
              path: '/api/auctions/:id/lots/:lotId/max-bids/:maxBidId',
              params: { id: '', lotId: '', maxBidId: '' },
              // clerking.manage. The lot is now checked against the auction
              // (`within`) — before BL-1833 PR 3b-2 it was not; the max-bid-in-lot
              // check stays in MaxBidCorrectionService.
              // BL-2132 (ADR 027 — Response Contract): both verbs answer a DTO.
              methods: {
                PATCH: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<MaxBidCorrectionDto>() },
                DELETE: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<MaxBidCancelDto>() },
              },
            }),
        },
        // BL-1833 PR 3b-1: `open` and `withdraw` deleted — neither client
        // wrapper had an importer; both transitions run through POST
        // /api/lots/batch (ADR 044 — Must Consolidate).
        reserve: {
          index: defineAdminRoute({
              path: '/api/auctions/:id/lots/:lotId/reserve',
              params: { id: '', lotId: '' },
              // clerking.manage, as the handler checked by hand. The lock override
              // (who may change a reserve mid-sale) stays a handler-side role read.
              // BL-2127 (ADR 027 — Response Contract): answers a DTO; BL-2112
              // adds the reserve-lifted send it queued.
              methods: {
                PATCH: { grant: 'lot.hammer', tenant: LOT_IN_AUCTION, response: responseType<LotReserveDto>() },
              },
            }),
          /**
           * BL-2112 — who the Update reserve dialog's "Tell bidders the reserve is
           * lifted" box would reach: counts from the SAME query the send uses
           * (ADR 057 — Operator Sends § 2). `lot.read` is the clerk mapping's read
           * grant (clerking.view has none). It is wider than the PATCH's
           * `lot.hammer`: Accounting holds it too, and reads counts only.
           */
          lift_audience: defineAdminRoute({
              path: '/api/auctions/:id/lots/:lotId/reserve/lift-audience',
              params: { id: '', lotId: '' },
              methods: {
                GET: { grant: 'lot.read', tenant: LOT_IN_AUCTION, response: responseType<ReserveLiftAudienceDto>() },
              },
            }),
        },
      },
    },
    // BL-1733: `open_now` + `open_pre_bidding` retired — duplicate HTTP shapes of
    // `transition { open | open-pre-bidding }` (BL-1294 residue, ADR 044).
    post_auction: {
      // BL-1546 (AC 10): `customers.search` retired — the Change buyer / Sell to
      // buyer pickers use `/api/customers/search` (ADR 044 — Must Consolidate).
      // BL-1547: `index` (GET /api/auctions/:id/post-auction, the tab's
      // aggregate read + 5-second poll) retired with the Post-auction tab. The
      // per-lot corrections below keep their paths — declared residue. BL-2059
      // folded `swap` into `lots.swap_buyer` (a selection of one).
      lots: {
        lotId: {
          reverse_sale: defineAdminRoute({
              path: '/api/auctions/:id/post-auction/lots/:lotId/reverse-sale',
              params: { id: '', lotId: '' },
              // BL-1445: GET serves the modal's own gate context, so the modal
              // takes an id and self-fetches rather than being handed four
              // domain values by whichever surface opened it (ADR 029).
              // BUG-1413: POST carries its DTO too, so the client stops asserting
              // an incomplete `<T>` at the call site (ADR 027 guarantee 1).
              methods: {
                POST: { grant: 'lot.correct_sale', tenant: LOT_IN_AUCTION, response: responseType<ReverseSaleDto>() },
                // Read by LotEditModal's dropdown from any surface that opens it,
                // so it takes the modal's read set (today: any tenant member).
                GET: { grant: LOT_EDIT_MODAL_READ, tenant: LOT_IN_AUCTION, response: responseType<ReverseSaleContextDto>() },
              },
            }),
          reopen: defineAdminRoute({
              path: '/api/auctions/:id/post-auction/lots/:lotId/reopen',
              params: { id: '', lotId: '' },
              methods: { POST: { grant: 'lot.correct_sale', tenant: LOT_IN_AUCTION, response: responseType<ReopenLotDto>() } },
            }),
          sell: defineAdminRoute({
              path: '/api/auctions/:id/post-auction/lots/:lotId/sell',
              params: { id: '', lotId: '' },
              // BUG-1462: the DTO rides the registry so the route's `respond()`
              // and the client's inferred type reference one declaration — which
              // is what gives the service's partial-failure `warnings` a typed
              // path to the envelope's `warning` (ADR 027 guarantees 1 + 2).
              methods: { POST: { grant: 'lot.correct_sale', tenant: LOT_IN_AUCTION, response: responseType<SellLotDto>() } },
            }),
        },
      },
    },
    registrations: {
      // BL-1667: the POST now carries its DTO (ADR 027) — the union body
      // (existing customer | create + register) is declared beside it in
      // `schemas/bid-registration.ts`.
      index: defineAdminRoute({
          path: '/api/auctions/:id/registrations',
          params: { id: '' },
          methods: {
            // BL-1833 AC 10 — add-buyer is the same decision as approving one.
            POST: {
              grant: 'auction.approve_bidders',
              tenant: { from: 'record', table: 'auction', param: 'id' },
              response: responseType<AdminAddBuyerResultDto>(),
            },
          },
        }),
      /**
       * BL-1556 — one enriched registration, so the approval card can fetch its
       * own copy by id (ADR 029 — Self-Contained View Units) instead of being
       * handed an object by whichever list is on screen. Returns the SAME
       * `AuctionBidderDto` the Bidders-tab list returns, from the same
       * enrichment pipeline.
       */
      detail: defineAdminRoute({
          path: '/api/auctions/:id/registrations/:registrationId',
          params: { id: '', registrationId: '' },
          methods: {
            GET: {
              grant: 'auction.read',
              tenant: { from: 'record', table: 'auction', param: 'id' },
              response: responseType<AuctionBidderDetailDto>(),
            },
          },
        }),
      terms_acceptances: defineAdminRoute({
          path: '/api/auctions/:id/registrations/:registrationId/terms-acceptances',
          params: { id: '', registrationId: '' },
          // Response DTO carrier (ADR 027 / BL-1313 + BL-1364): what this buyer
          // agreed to, which version the auction is on now, and — when nothing
          // was recorded — why. The comparison is server-side so no client
          // re-derives "do they need to re-accept?". POST (BL-1364) records an
          // offline attestation and returns the SAME refreshed payload, so the
          // modal updates from the mutation's own response (rule #34).
          methods: {
            GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<RegistrationTermsAcceptancesPayload>() },
            POST: { grant: 'auction.update', tenant: AUCTION, response: responseType<RegistrationTermsAcceptancesPayload>() },
          },
        }),
      terms_acceptance_id: defineAdminRoute({
          path: '/api/auctions/:id/registrations/:registrationId/terms-acceptances/:acceptanceId',
          params: { id: '', registrationId: '', acceptanceId: '' },
          // BL-1364 — retract one staff attestation (`{ retracted: true }`).
          // Returns the refreshed block payload, same as the POST above.
          methods: { PATCH: { grant: 'auction.update', tenant: AUCTION, response: responseType<RegistrationTermsAcceptancesPayload>() } },
        }),
      // BL-2590 — "View signed copy": a short-lived link to the signed terms
      // staff attached to an offline attestation. Read grant, like the block's
      // GET: the signed copy is the evidence behind the acceptance that grant
      // already reads, and today only Admin and Accounting (read-only) hold it.
      // Attaching one needs `auction.approve_bidders` (presign) or
      // `auction.update` (record).
      terms_acceptance_proof: defineAdminRoute({
          path: '/api/auctions/:id/registrations/:registrationId/terms-acceptances/:acceptanceId/proof',
          params: { id: '', registrationId: '', acceptanceId: '' },
          methods: { GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<TermsAcceptanceProofUrlDto>() } },
        }),
    },
    clerk: {
      /**
       * BL-1255 — the STATIC half of the clerk console's Items catalog.
       *
       * Separate from `id.lots` deliberately: that endpoint serves the
       * Sequencing surface and returns a far richer per-lot projection. The
       * catalog renders a 392px column and needs four fields, so per
       * `architecture.md` ("select only the fields the view actually renders")
       * it gets its own query rather than a flag on a heavier one.
       */
      catalog: defineAdminRoute({
          path: '/api/auctions/:id/clerk/catalog',
          params: { id: '' },
          // clerking.view has no grant; `lot.read` (auctions.view) is the nearest.
          methods: { GET: { grant: 'lot.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<ClerkCatalogDto>() } },
        }),
      /**
       * BL-1556 — the bidder rail's roster: search-only, capped at
       * `CLERK_RAIL_ROSTER_LIMIT`, with server-resolved `isOnline` and a scoped
       * activity aggregate. The cap is named rather than written out because it
       * is one number for both modes now (BL-1556 amendment 2026-09-08 collapsed
       * the 100-row search cap into the 50-row recent one) and a second place
       * stating it is a second place to go stale.
       *
       * Separate from `/api/auctions/:id/bidders` (the Bidders TAB's read)
       * deliberately: that one is unpaged, newest-first and carries the
       * required-information scorecard the tab renders. The rail needs seven
       * fields for a bounded page.
       */
      rail_roster: defineAdminRoute({
          path: '/api/auctions/:id/clerk/rail-roster',
          params: { id: '' },
          methods: { GET: { grant: 'auction.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<ClerkRailRosterDto>() } },
        }),
      /**
       * BL-1561 — the place-bid modal's bidder PICKER: server-side search over
       * the auction's register, capped, with an empty query returning a bounded
       * first page rather than every registrant.
       *
       * Separate from `rail_roster` above deliberately, and not a flag on it:
       * that read serves the rail's DECIDED rows and excludes `pending`
       * outright (pending is the rail's own pinned band). The picker needs the
       * approved rows a clerk may bid for PLUS a count of the unapproved
       * matches it is not showing — the BUG-1392 signal that stops a clerk
       * concluding a bidder was never registered and creating a duplicate
       * mid-sale. Different question, different projection; the shared search
       * predicate is reused at the repository layer instead.
       */
      bidder_search: defineAdminRoute({
          path: '/api/auctions/:id/clerk/bidder-search',
          params: { id: '' },
          methods: { GET: { grant: 'auction.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<ClerkBidderSearchDto>() } },
        }),
      /**
       * BL-1556 — the bidder rail's PENDING queue: oldest-first, keyset-paged,
       * with the whole-queue total.
       *
       * Serves both the inline band (`limit=3`) and the review-all modal (a
       * page walked by `cursor`), so the two cannot disagree about what
       * "oldest first" means. Keyset rather than offset is an AC 7 CORRECTNESS
       * requirement: approving removes a row, and offset paging then steps over
       * whoever moved up into the vacated slot.
       */
      pending_registrations: defineAdminRoute({
          path: '/api/auctions/:id/clerk/pending-registrations',
          params: { id: '' },
          methods: {
            GET: { grant: 'auction.read', tenant: { from: 'record', table: 'auction', param: 'id' }, response: responseType<ClerkPendingRegistrationsPageDto>() },
          },
        }),
    },
    // BL-1180: the dedicated paginated Lot-Order read (ADR 029 self-contained
    // view unit) — 50 ring-assigned lots/page + the ring-less tray + the ring
    // roster with true counts. Separate from `rings` (which stays unpaged for
    // print + ring config).
    lotOrder: defineAdminRoute({
        path: '/api/auctions/:id/lot-order',
        params: { id: '' },
        methods: { GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<LotOrderPageDto>() } },
      }),
    // BL-1180 Stage 3: resolve a lot number → the page containing it, so the
    // "Lot ___" jump box (AC 22) and a cross-page typed move (AC 7) can navigate.
    lotOrderLocate: defineAdminRoute({
        path: '/api/auctions/:id/lot-order/locate',
        params: { id: '' },
        methods: { GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<LotOrderLocateDto>() } },
      }),
    rings: {
      index: defineAdminRoute({
          path: '/api/auctions/:id/rings',
          params: { id: '' },
          // Response DTO carrier (ADR 027 / BL-855): GET returns the rings +
          // nested lots aggregate. BL-1833 PR 3c-1 deleted POST (create one
          // ring) and PUT (batch re-sort): Configure rings adds, removes and
          // re-sorts through `roster` PUT, and nothing else called them.
          methods: {
            GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<AuctionRingsListDto>() },
            // BL-1166 AC 21: turn rings off for the whole auction in ONE
            // request. Untyped — the body is `{success, removedRingCount}` with
            // no `data` key.
            DELETE: { grant: RING_WRITE, tenant: AUCTION },
          },
        }),
      // BL-1549: the Configure rings modal's own read + write. Deliberately a
      // sibling of `index` rather than more verbs on it — `index`'s GET is the
      // whole-catalog aggregate (rings + every lot), and the modal needs four
      // fields per ring and no lots at all. See the DTO's header for why that
      // is a separate projection rather than a wider one.
      roster: defineAdminRoute({
          path: '/api/auctions/:id/rings/roster',
          params: { id: '' },
          methods: {
            // A read: in-tenant reads are broad (it used to demand the write pair).
            GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<RingRosterConfigDto>() },
            // One composite request for the whole roster: adds, removals and
            // per-ring field changes applied in ONE transaction
            // (server-side-orchestration.md Rule 1). Returns the roster as it
            // stands after the write, so the modal needs no follow-up GET.
            PUT: { grant: RING_WRITE, tenant: AUCTION, response: responseType<RingRosterConfigDto>() },
          },
        }),
    },
    // BL-1453: the roster is lot-derived UNION checked-in, so add/remove is back
    // — but as `POST` on the collection and `DELETE` on the member, not the
    // legacy free-text `PATCH /api/auctions/:id/sellers` that BL-1013 deleted
    // (that one existed only to write the display-only `commission` TEXT column
    // settlement never read, and it is not coming back). Both new verbs are
    // gated on AUCTIONS_MANAGE and both return the fresh roster.
    //
    // BL-1145 nested `sellerId` so the per-auction commission override could
    // hang off it — the same shape BL-984 used for the ring BP override.
    sellers: {
      index: defineAdminRoute({
          path: '/api/auctions/:id/sellers',
          params: { id: '' },
          methods: {
            // Check a seller in — body `{ customerId }`, response is the roster.
            // The commission-redaction check (customers.manage | accounting_tab)
            // stays in the handler: it shapes the response, it never refuses.
            POST: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionSellerRosterResult>() },
          },
        }),
      sellerId: {
        index: defineAdminRoute({
            path: '/api/auctions/:id/sellers/:sellerId',
            params: { id: '', sellerId: '' },
            methods: {
              // Remove a seller — response is the roster after the removal.
              DELETE: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionSellerRosterResult>() },
            },
          }),
        commission: defineAdminRoute({
            path: '/api/auctions/:id/sellers/:sellerId/commission',
            params: { id: '', sellerId: '' },
            // Response DTO carrier (ADR 027 / BL-1145): the stored override plus
            // the already-settled lot count that the Auction Settings card
            // (BL-1149) renders as a warn-don't-block notice.
            methods: {
              // BL-1149: the override modal self-fetches its own copy.
              // customers.manage on both, as today: commission terms are not a
              // broad in-tenant read.
              GET: { grant: 'customer.update', tenant: AUCTION, response: responseType<AuctionSellerCommissionReadResult>() },
              PUT: { grant: 'customer.update', tenant: AUCTION, response: responseType<AuctionSellerCommissionWriteResult>() },
            },
          }),
      },
    },
    // `terms.index` (`GET /api/auctions/:id/terms`, the version-history read)
    // was deleted in BL-1358: its POST verb went in BL-1312, and its one
    // remaining consumer — the approval modal's wording viewer — now opens the
    // terms reader through `terms_documents.version` (ADR 047 § 2.1), leaving
    // nothing that reads a bespoke `{ current, history }` envelope.
    /**
     * BL-1312 — the auction↔terms pointer. ONE write path (AC 17): `PUT`
     * attaches a published version or forks an auction-scoped copy, depending
     * on the discriminated body, and both are permission-gated and audited.
     */
    terms_version: {
      index: defineAdminRoute({
          path: '/api/auctions/:id/terms-version',
          params: { id: '' },
          methods: {
            GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<AuctionTermsAttachmentDto>() },
            PUT: { grant: 'auction.update', tenant: AUCTION, response: responseType<AuctionTermsAttachmentDto>() },
          },
        }),
      impact: defineAdminRoute({
          path: '/api/auctions/:id/terms-version/impact',
          params: { id: '' },
          methods: { GET: { grant: 'auction.read', tenant: AUCTION, response: responseType<TermsChangeImpactDto>() } },
        }),
    },
    watchers: defineAdminRoute({
        path: '/api/auctions/:id/watchers',
        params: { id: '' },
        methods: { GET: { grant: 'lot.read', tenant: AUCTION, response: responseType<AuctionWatchersDto>() } },
      }),
  },
  list: defineAdminRoute({
      path: '/api/auctions',
      params: {},
      // ADR 027 / BL-901: GET returns one page of lean `AuctionListItem`s plus the total (BL-2769:
      // the /auctions index pages 50 at a time); POST (create) returns the full `Auction`.
      methods: {
        // Read from outside the auction pages too: the relist picker Catalogers
        // use from /inventory and from customer pages Accounting can open — so
        // the lot-edit-modal read set, not auction.read.
        GET: {
          grant: LOT_EDIT_MODAL_READ,
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<AuctionListPage>(),
        },
        POST: {
          grant: 'auction.update',
          tenant: { from: 'input', field: 'tenantId', in: 'body' },
          response: responseType<Auction>(),
        },
      },
    }),
};
